import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SlServicesService } from 'src/app/shared/sl-services/sl-services.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { KnowledgeDocument } from '../ai-settings/ai-settings.component';

interface DialogData {
    document: KnowledgeDocument | null;
}

@Component({
    selector: 'app-knowledge-document-modal',
    templateUrl: './knowledge-document-modal.component.html',
    styleUrls: ['./knowledge-document-modal.component.scss']
})
export class KnowledgeDocumentModalComponent implements OnInit {
    form: FormGroup;
    isEditMode: boolean = false;
    isSaving: boolean = false;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<KnowledgeDocumentModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogData,
        private services: SlServicesService,
        private utilities: UtilitiesService
    ) {
        this.isEditMode = !!data?.document?.id;

        this.form = this.fb.group({
            title: [data?.document?.title || '', [Validators.required, Validators.minLength(3)]],
            content: [data?.document?.content || '', [Validators.required, Validators.minLength(10)]]
        });
    }

    ngOnInit(): void {}

    get isFormValid(): boolean {
        return this.form.valid && !this.isSaving;
    }

    async onSubmit(): Promise<void> {
        if (!this.form.valid || this.isSaving) {
            this.form.markAllAsTouched();
            return;
        }

        this.isSaving = true;

        try {
            const title = this.form.get('title')?.value?.trim();
            const content = this.form.get('content')?.value?.trim();

            let result;

            if (this.isEditMode) {
                result = await this.services.callService('updateKnowledgeDocument', {
                    id: this.data.document.id,
                    title,
                    content
                });
            } else {
                result = await this.services.callService('createKnowledgeDocument', {
                    title,
                    content
                });
            }

            if (result.success) {
                this.utilities.notifySuccess(
                    this.isEditMode
                        ? 'Knowledge document updated successfully'
                        : 'Knowledge document created successfully'
                );
                this.dialogRef.close({ saved: true, document: result.document });
            }
        } catch (error) {
            console.error('Error saving knowledge document:', error);
            this.utilities.notifyError(
                this.isEditMode
                    ? 'Failed to update knowledge document'
                    : 'Failed to create knowledge document'
            );
        } finally {
            this.isSaving = false;
        }
    }

    onCancel(): void {
        this.dialogRef.close({ saved: false });
    }
}
